import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import {
  BreadcrumbSeparator,
  Breadcrumb,
  Breadcrumbs,
} from "@tokenterminal/core/ui/navigation/breadcrumbs/Breadcrumbs"
import { type RouterOutputs } from "@tokenterminal/core/utils/trpc"
import { toId } from "modules/market-sectors/utils"
import { useAvailableEcosystemMetricIds } from "modules/projects/hooks/useAvailableEcosystemMetricIds"
import { useEffect, useRef } from "react"
import { EcosystemBadge } from "../MetricsListPage/MetricsListPage"
import OverviewTab from "./OverviewTab"

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

type MetricDetailPageProps = {
  metric: RouterOutputs["metrics"]["getMetricConfiguration"]
}

const MetricDetailPage = ({ metric }: MetricDetailPageProps) => {
  const { data: ecosystemMetricIds = [], isLoading } =
    useAvailableEcosystemMetricIds()

  const isEcosystemMetric = ecosystemMetricIds.includes(toId(metric.slug))

  const headerRef = useRef<HTMLDivElement>(null)
  const stickyHeaderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!stickyHeaderRef.current) {
      return
    }

    const handleScroll = () => {
      if (headerRef.current && stickyHeaderRef.current) {
        const headerBottom = headerRef.current.getBoundingClientRect().bottom
        stickyHeaderRef.current.setAttribute(
          "data-scrolled",
          headerBottom < 0 ? "true" : "false"
        )
        stickyHeaderRef.current.inert = headerBottom < 0 ? false : true
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Can be extended to support more tabs in the future
  const activeTabId = "overview"

  const content: Record<string, React.ReactNode> = {
    overview: (
      <OverviewTab metric={metric} isEcosystemMetric={isEcosystemMetric} />
    ),
  }

  const activeTab = content[activeTabId]

  return (
    <div className="w-[1540px] max-w-full mx-auto relative">
      {/* Sticky Header */}
      <div
        ref={stickyHeaderRef}
        className={`fixed top-0 left-0 right-0 bg-bg-default border-b border-solid border-border-default transform transition-transform duration-300 z-50 data-[scrolled=true]:translate-y-0 -translate-y-full`}
        inert
      >
        {!isLoading && (
          <div className="w-[1540px] px-4 mx-auto">
            <div className="flex items-center py-3 justify-between">
              <div className="flex items-center gap-2">
                <Breadcrumbs>
                  <Breadcrumb href="/metrics">Metrics</Breadcrumb>
                  <BreadcrumbSeparator />
                  <Breadcrumb>{metric.title}</Breadcrumb>
                </Breadcrumbs>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Header Section */}
      <div ref={headerRef}>
        {!isLoading && (
          <div className="px-4 max-w-full mx-auto">
            <div className="flex flex-col gap-4 mb-2">
              <Breadcrumbs className="my-2">
                <Breadcrumb href="/metrics">Metrics</Breadcrumb>
                <BreadcrumbSeparator />
                <Breadcrumb>{metric.title}</Breadcrumb>
              </Breadcrumbs>

              <div className="flex flex-col items-start gap-1 mt-5">
                {isEcosystemMetric && (
                  <EcosystemBadge size="small" className="mb-1">
                    Ecosystem metric
                  </EcosystemBadge>
                )}
                <Heading level={1}>{metric.title}</Heading>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      {/* <div className="px-4 max-w-full mx-auto">{tabs}</div>
      <div className="w-full h-px bg-border-default" /> */}

      {/* Main Content */}
      {!metric && !isLoading && <div>Metric not found</div>}
      {/* Placeholder to avoid footer jumping when loading. It loads fast enough so
      we don't need a loading indicator. */}
      {isLoading && <div className="h-[2000px]" />}
      {!isLoading && <div className="px-4 max-w-full mx-auto">{activeTab}</div>}
    </div>
  )
}

export default MetricDetailPage
